import React from "react";
import styled from "styled-components";
import { Github, ExternalLink } from "lucide-react";
import { PortfolioItem } from "../types";
import { motion, AnimatePresence } from "framer-motion";

interface ProjectCardProps {
  menuItem: PortfolioItem[];
}

const cardVariants = {
  hidden: { opacity: 0, y: 30, scale: 0.95 },
  visible: { opacity: 1, y: 0, scale: 1 },
  exit: { opacity: 0, scale: 0.9, transition: { duration: 0.2 } },
};

const ProjectCard: React.FC<ProjectCardProps> = ({ menuItem }) => {
  return (
    <GridStyled>
      <AnimatePresence>
        {menuItem.map((item, index) => (
          <motion.div
            key={item.id}
            layout
            variants={cardVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            transition={{
              duration: 0.4,
              delay: index * 0.05,
              ease: [0.25, 0.1, 0.25, 1],
            }}
          >
            <Card>
              <div className="card-image">
                <img src={item.image} alt={item.title} loading="lazy" />
                <div className="overlay">
                  <a
                    href={item.link1}
                    target="_blank"
                    rel="noreferrer"
                    className="link-btn"
                    title="GitHub"
                  >
                    <Github size={20} />
                  </a>
                  {item.link2 && item.link2 !== item.link1 && (
                    <a
                      href={item.link2}
                      target="_blank"
                      rel="noreferrer"
                      className="link-btn"
                      title="Live Demo"
                    >
                      <ExternalLink size={20} />
                    </a>
                  )}
                </div>
              </div>
              <div className="card-body">
                <span className="category">{item.category}</span>
                <h5>{item.title}</h5>
                <p>{item.text}</p>
              </div>
            </Card>
          </motion.div>
        ))}
      </AnimatePresence>
    </GridStyled>
  );
};

const GridStyled = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;

  @media screen and (max-width: 1000px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media screen and (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: var(--card-bg);
  border: 1px solid var(--card-border);
  border-radius: 16px;
  overflow: hidden;
  transition: all 0.3s ease;

  &:hover {
    border-color: var(--card-hover-border);
    transform: translateY(-4px);
    box-shadow: var(--shadow-lg);

    .card-image .overlay {
      opacity: 1;
    }

    .card-image img {
      transform: scale(1.05);
    }
  }

  .card-image {
    position: relative;
    overflow: hidden;
    aspect-ratio: 16 / 10;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      transition: transform 0.4s ease;
    }

    .overlay {
      position: absolute;
      inset: 0;
      background: rgba(0, 0, 0, 0.6);
      backdrop-filter: blur(4px);
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.75rem;
      opacity: 0;
      transition: opacity 0.3s ease;

      .link-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 2.75rem;
        height: 2.75rem;
        border-radius: 50%;
        background: rgba(255, 255, 255, 0.15);
        border: 1px solid rgba(255, 255, 255, 0.2);
        color: white;
        transition: all 0.25s ease;

        &:hover {
          background: var(--primary-color);
          border-color: var(--primary-color);
          transform: scale(1.1);
        }
      }
    }
  }

  .card-body {
    padding: 1.25rem;

    .category {
      display: inline-block;
      font-size: 0.7rem;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.08em;
      color: var(--primary-color);
      background: var(--background-light-color-2);
      padding: 0.2rem 0.6rem;
      border-radius: 6px;
      margin-bottom: 0.5rem;
    }

    h5 {
      font-size: 1.05rem;
      font-weight: 700;
      margin-bottom: 0.4rem;
      line-height: 1.3;
    }

    p {
      font-size: 0.825rem;
      color: var(--font-light-color);
      line-height: 1.6;
      display: -webkit-box;
      -webkit-line-clamp: 3;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }
  }
`;

export default ProjectCard;
